import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, handleAPIError } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Helper functions to get/set tokens with proper storage
  const getStoredToken = () => {
    return localStorage.getItem('brixbot_token') || sessionStorage.getItem('brixbot_token');
  };

  const getStoredUser = () => {
    const userFromLocal = localStorage.getItem('brixbot_user');
    const userFromSession = sessionStorage.getItem('brixbot_user');
    return userFromLocal || userFromSession;
  };

  const storeAuthData = (token, userData, remember = false) => {
    if (remember) {
      // Store in localStorage for persistence across browser sessions
      localStorage.setItem('brixbot_token', token);
      localStorage.setItem('brixbot_user', JSON.stringify(userData));
      // Clear from sessionStorage to avoid conflicts
      sessionStorage.removeItem('brixbot_token');
      sessionStorage.removeItem('brixbot_user');
    } else {
      // Store in sessionStorage for current session only
      sessionStorage.setItem('brixbot_token', token);
      sessionStorage.setItem('brixbot_user', JSON.stringify(userData));
      // Clear from localStorage to avoid conflicts
      localStorage.removeItem('brixbot_token');
      localStorage.removeItem('brixbot_user');
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('brixbot_token');
    localStorage.removeItem('brixbot_user');
    sessionStorage.removeItem('brixbot_token');
    sessionStorage.removeItem('brixbot_user');
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      const savedToken = getStoredToken();
      const savedUser = getStoredUser();

      if (savedToken && savedUser) {
        try {
          // Set token and user first
          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          
          // Verify token is still valid
          const response = await authAPI.getCurrentUser();
          const currentUser = response.data;
          
          // Update user data if different
          if (JSON.stringify(currentUser) !== savedUser) {
            // Determine which storage was used and update accordingly
            const isInLocalStorage = localStorage.getItem('brixbot_token');
            if (isInLocalStorage) {
              localStorage.setItem('brixbot_user', JSON.stringify(currentUser));
            } else {
              sessionStorage.setItem('brixbot_user', JSON.stringify(currentUser));
            }
            setUser(currentUser);
          }
        } catch (error) {
          console.error('Token validation failed during initialization:', error);
          // Only clear auth data if the error indicates invalid token (401)
          if (error.response?.status === 401) {
            console.log('Token expired or invalid, clearing auth data');
            clearAuthData();
            setToken(null);
            setUser(null);
          } else {
            console.log('Non-auth error during initialization, keeping existing auth state');
            // Keep the saved token and user data even if validation fails due to network issues
            setToken(savedToken);
            setUser(JSON.parse(savedUser));
          }
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials, rememberMe = false) => {
    try {
      setLoading(true);
      
      // Login API call
      const loginResponse = await authAPI.login(credentials);
      const { access_token } = loginResponse.data;
      
      // CRITICAL FIX: Store token immediately so axios interceptor can use it
      storeAuthData(access_token, {}, rememberMe);
      setToken(access_token);
      
      // Now get user data with the stored token
      const userResponse = await authAPI.getCurrentUser();
      const userData = userResponse.data;
      
      // Update stored data with complete user info
      storeAuthData(access_token, userData, rememberMe);
      setUser(userData);
      
      setLoading(false);
      return { success: true, user: userData };
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Login failed');
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      
      await authAPI.register(userData);
      
      // Auto-login after registration (default to session storage for security)
      const loginResult = await login({
        email: userData.email,
        password: userData.password,
      }, false); // Don't remember by default for new registrations
      
      return loginResult.success 
        ? { success: true, user: loginResult.user }
        : { success: false, error: loginResult.error };
        
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Registration failed');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuthData();
    setToken(null);
    setUser(null);
    
    // Redirect to login page after logout
    window.location.href = '/login';
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    
    // Update in the same storage that was used for login
    const isInLocalStorage = localStorage.getItem('brixbot_token');
    if (isInLocalStorage) {
      localStorage.setItem('brixbot_user', JSON.stringify(newUserData));
    } else {
      sessionStorage.setItem('brixbot_user', JSON.stringify(newUserData));
    }
    
    setUser(newUserData);
  };

  const isAdmin = () => user?.role === 'admin';
  const isEmployee = () => user?.role === 'employee';
  const isCustomer = () => user?.role === 'customer';
  const isDemo = () => user?.role === 'demo';
  
  const canAccessAdminFeatures = () => isAdmin();
  const canAccessEmployeeFeatures = () => isAdmin() || isEmployee();
  const canCreateChatbots = () => isCustomer() || isDemo();

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAdmin,
    isEmployee,
    isCustomer,
    isDemo,
    canAccessAdminFeatures,
    canAccessEmployeeFeatures,
    canCreateChatbots,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;